import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { contentAPI } from '../../services/api';

const ServicesPillarsEditor = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const res = await contentAPI.get('services_pillars'); setData(res.data || {}); }
    catch { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try { await contentAPI.update('services_pillars', data); toast.success('Services saved!'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updatePillar = (idx, field, value) => {
    const pillars = [...(data.pillars || [])];
    pillars[idx] = { ...pillars[idx], [field]: value };
    setData({ ...data, pillars });
  };

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <p className="text-sm text-gray-500">Edit the three main service pillars displayed on the Services page and homepage.</p>
      {(data.pillars || []).map((p, idx) => (
        <Card key={idx}>
          <CardHeader><CardTitle>{p.title || `Pillar ${idx + 1}`}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Title</label>
                <Input value={p.title || ''} onChange={e => updatePillar(idx, 'title', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Icon (Sun, FileCheck, Scale)</label>
                <Input value={p.icon || ''} onChange={e => updatePillar(idx, 'icon', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Link Path</label>
                <Input value={p.link || ''} onChange={e => updatePillar(idx, 'link', e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Summary</label>
              <Textarea rows={2} value={p.summary || ''} onChange={e => updatePillar(idx, 'summary', e.target.value)} />
            </div>
          </CardContent>
        </Card>
      ))}
      <Button onClick={save} disabled={saving} className="w-full" data-testid="save-pillars">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Service Pillars
      </Button>
    </div>
  );
};

export default ServicesPillarsEditor;
